import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import HeroSection from './HeroSection';
import CounterSection from './CounterSection';
import MSMEStatsSection from './MSMEStatsSection';
import TrainingKitSection from './TrainingKitSection';
import BenefitsSection from './BenefitsSection';
import AboutSection from './AboutSection';
import VideoTestimonialsSection from './VideoTestimonialsSection';
import TestimonialsSection from './TestimonialsSection';
import PricingSection from './PricingSection';
import PaymentModal from './PaymentModal';
import PaymentCTA from './PaymentCTA';
import PaymentPage from './PaymentPage';
import logo from '../../assets/image/logo.png';
import FooterSection from './FooterSection';
import ContactSection from '../CallingScripts/ContactSection';

function BlockedPaymentsBack() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [showPaymentPage, setShowPaymentPage] = useState(false);
  const [selectedPrice, setSelectedPrice] = useState(4000);
  const [pricing, setPricing] = useState({
    price1Friend: 4000
  });
  const [showStickyCTA, setShowStickyCTA] = useState(false);
  const heroCTARef = useCallback((node) => {
    if (!node) return;
    const observer = new IntersectionObserver(
      ([entry]) => setShowStickyCTA(!entry.isIntersecting),
      { threshold: 0 }
    );
    observer.observe(node);
  }, []);
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const API_BASE_URL = process.env.REACT_APP_PAYMENT_API_URL || 'http://localhost:5005/api';

  // Fetch pricing from admin panel
  const fetchPricing = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/payments/page-pricing/active/blocked-payments`);
      const data = await response.json();
      if (data.success && data.pricing) {
        setPricing({
          price1Friend: parseFloat(data.pricing.price_1_friend) || 4000
        });
      }
    } catch (err) {
      console.error('Failed to fetch pricing:', err);
    }
  }, [API_BASE_URL]);

  useEffect(() => {
    fetchPricing();
  }, [fetchPricing]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleOpenPayment = () => {
    setIsPaymentModalOpen(true);
  };

  const handleClosePayment = () => {
    setIsPaymentModalOpen(false);
  };

  const handleOpenPaymentPage = (price) => {
    setSelectedPrice(price);
    setShowPaymentPage(true);
    document.body.style.overflow = 'hidden';
  };

  const handleClosePaymentPage = () => {
    setShowPaymentPage(false);
    document.body.style.overflow = 'auto';
  };

  return (
    <div className="min-h-screen bg-black text-white scroll-smooth">
      {/* Sticky Header */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled 
          ? 'bg-black/95 backdrop-blur-xl border-b border-gray-800 py-3' 
          : 'bg-transparent py-5'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <div className="flex items-center gap-4 group">
            <div className="relative">
              <div className={`absolute -inset-1 bg-gradient-to-r from-amber-500 to-orange-500 rounded-2xl blur opacity-0 group-hover:opacity-50 transition-opacity duration-500 ${isScrolled ? '' : 'group-hover:opacity-30'}`}></div>
              <div className="relative w-14 h-14 sm:w-16 sm:h-16 bg-white rounded-2xl p-2 shadow-lg">
                <img 
                  src={logo} 
                  alt="Rushneek Singh Logo" 
                  className="w-full h-full rounded-xl object-contain"
                />
              </div>
            </div>
            <div className="hidden sm:block">
              <span className="text-xl font-bold text-white group-hover:text-amber-400 transition-colors">Rushneek Singh</span>
              <span className="block text-sm text-amber-400 font-medium">India's No. 1 B2B Payment Recovery Coach</span>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Buttons removed for cleaner header */}
          </div>
        </div>
      </header>

      {/* Sticky CTA Bar */}
      <div className={`fixed bottom-0 left-0 right-0 z-[60] transition-all duration-300 ${showStickyCTA ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0 pointer-events-none'}`}>
        <div className="bg-black/90 backdrop-blur-xl border-t border-amber-500/30 shadow-[0_-4px_20px] shadow-amber-500/10">
          <div className="max-w-2xl mx-auto px-4 py-3">
            <button
              onClick={() => handleOpenPaymentPage(pricing.price1Friend)}
              className="group/btn relative inline-flex items-center justify-center w-full py-4 px-6 text-base font-bold text-black bg-gradient-to-r from-amber-400 via-yellow-400 to-orange-500 rounded-xl shadow-lg shadow-amber-500/30 overflow-hidden transition-all duration-300 hover:scale-[1.02] hover:shadow-amber-500/50"
            >
              <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/30 to-white/0 -translate-x-full group-hover/btn:translate-x-full transition-transform duration-700"></span>
              <span className="relative flex items-center gap-2">
                Yes, I Want This
                <svg className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main>
        <HeroSection onOpenPayment={handleOpenPayment} onOpenPaymentPage={handleOpenPaymentPage} pricing={pricing} ctaRef={heroCTARef} />
        {/* <PaymentCTA onOpenPayment={handleOpenPayment} /> */}
        <CounterSection />
        {/* <MSMEStatsSection /> */}
                <PaymentCTA onOpenPayment={handleOpenPayment} onOpenPaymentPage={handleOpenPaymentPage} pricing={pricing} />

        <TrainingKitSection onOpenPayment={handleOpenPayment} />
        <PaymentCTA onOpenPayment={handleOpenPayment} onOpenPaymentPage={handleOpenPaymentPage} pricing={pricing} />
        <BenefitsSection />
        <PaymentCTA onOpenPayment={handleOpenPayment} onOpenPaymentPage={handleOpenPaymentPage} pricing={pricing} />
        <AboutSection />
        <PaymentCTA onOpenPayment={handleOpenPayment} onOpenPaymentPage={handleOpenPaymentPage} pricing={pricing} />
        {/* <PricingSection onOpenPayment={handleOpenPayment} /> */}
        {/* <PaymentCTA onOpenPayment={handleOpenPayment} /> */}

          <TestimonialsSection />
        <PaymentCTA onOpenPayment={handleOpenPayment} onOpenPaymentPage={handleOpenPaymentPage} pricing={pricing} />
        <VideoTestimonialsSection />
              <PaymentCTA onOpenPayment={handleOpenPayment} onOpenPaymentPage={handleOpenPaymentPage} pricing={pricing} />

        <ContactSection/>
        {/* <FooterSection /> */}
      </main>

      {/* Floating WhatsApp Button */}
      {/* <a
        href="https://wa.me/918920633120"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 group"
        aria-label="Chat on WhatsApp"
      >
        <div className="absolute -inset-2 bg-green-500 rounded-full blur-md opacity-50 group-hover:opacity-80 animate-pulse transition-opacity"></div>
        <div className="relative w-14 h-14 bg-green-500 hover:bg-green-400 rounded-full flex items-center justify-center shadow-2xl shadow-green-500/50 transition-all hover:scale-110">
          <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 24 24">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>
        </div>
      </a> */}

      {/* Back to Top Button */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className={`fixed bottom-6 left-6 z-50 group transition-all duration-300 ${
          isScrolled ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10 pointer-events-none'
        }`}
        aria-label="Back to top"
      >
        <div className="relative w-12 h-12 bg-gray-800 hover:bg-gray-700 rounded-full flex items-center justify-center shadow-xl border border-gray-700 transition-all hover:scale-110">
          <svg className="w-5 h-5 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
          </svg>
        </div>
      </button>

      {/* Global Styles */}
      <style jsx global>{`
        html {
          scroll-behavior: smooth;
        }
        
        /* Custom Scrollbar */
        ::-webkit-scrollbar {
          width: 10px;
        }
        ::-webkit-scrollbar-track {
          background: #000;
        }
        ::-webkit-scrollbar-thumb {
          background: linear-gradient(to bottom, #f59e0b, #f97316);
          border-radius: 10px;
        }
        ::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(to bottom, #fbbf24, #fb923c);
        }

        /* Selection */
        ::selection {
          background: rgba(251, 191, 36, 0.3);
          color: white;
        }

        /* Focus visible */
        :focus-visible {
          outline: 2px solid #f59e0b;
          outline-offset: 2px;
        }
      `}</style>

      {/* Payment Modal */}
      <PaymentModal 
        isOpen={isPaymentModalOpen} 
        onClose={handleClosePayment}
        amount={4000}
        productName="Blocked Payments Recovery Kit"
      />

      {/* Payment Page */}
      {showPaymentPage && (
        <PaymentPage 
          isOpen={showPaymentPage}
          selectedPrice={selectedPrice}
          onClose={handleClosePaymentPage}
        />
      )}
    </div>
  );
}

export default BlockedPaymentsBack;
